import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import axiosInstance from "api/axiosInstance";
import API from "api/endpoints";

function ExpenseManagementService() {
  const [isLoading, setIsLoading] = useState(false);
  const [managerName, setManagerName] = useState("");
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
      Date: "",
    Description: "",
    Charges: "",
    Receipt: null, 
    CalculationSheet: null, 
  });
  const [formDataError, setFormDataError] = useState({
    DateError: "",
    descriptionError: "",
    chargesError: "",
    receiptError: "",
    calculationSheetError: "",
  });

 

  /**  Handle input change */
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    setFormDataError((prev) => ({
      ...prev,
      [`${name}Error`]: value.trim() ? "" : `${name} is required.`,
    }));
  };

  /**  Handle file uploads */
  const handleUploadDocChange = (e) => handleFileUpload(e, "Receipt");
  const handleUploadCalculationDoc = (e) => handleFileUpload(e, "CalculationSheet");

  const handleFileUpload = (e, fieldName) => {
    const file = e.target.files[0];
    if (!file) return;

    // Check file size (max 10MB)
    const maxSize = 10 * 1024 * 1024; // 10MB in bytes
    if (file.size > maxSize) {
      toast.error("File size exceeds 10MB limit");
      e.target.value = ""; // Clear the file input
      return;
    }

    const allowedTypes = {
      Receipt: [
        "application/pdf",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      ],
      CalculationSheet: [
        "application/vnd.ms-excel", // .xls
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // .xlsx
        "text/csv", // .csv
      ],
    };

    const allowed = allowedTypes[fieldName] || [];
    const fileType = file.type;

    if (!allowed.includes(fileType)) {
      toast.error(
        `Invalid file format for ${fieldName}. Only ${
          fieldName === "calculationSheet" ? ".xls, .xlsx, .csv" : "PDF, DOC, DOCX"
        } allowed.`
      );
      e.target.value = ""; // Clear the file input
      return;
    }

    setFormData((prev) => ({ ...prev, [fieldName]: file }));
    setFormDataError((prev) => ({ ...prev, [`${fieldName}Error`]: "" }));
  };

  /**  Validate fields */
  const validateForm = () => {
    let valid = true;
    const errors = {};

    // Check all required fields
    const requiredFields = [
      "Date",
      "Description",
      "Charges",
      "Receipt",
      "CalculationSheet",
    ];

    requiredFields.forEach((field) => {
      if (!formData[field] || formData[field].toString().trim() === "") {
        errors[`${field}Error`] = `${field} is required.`;
        valid = false;
      }
    });

    // Additional validation for charges
    if (formData.Charges) {
      const chargesValue = parseFloat(formData.Charges);
      if (isNaN(chargesValue) || chargesValue <= 0) {
        errors.chargesError = "Charges must be a positive number.";
        valid = false;
      }
    }

    // File validation
    if (formData.Receipt && formData.Receipt.size > 10 * 1024 * 1024) {
      errors.receiptError = "Receipt file size must be less than 10MB.";
      valid = false;
    }

    if (formData.CalculationSheet && formData.CalculationSheet.size > 10 * 1024 * 1024) {
      errors.calculationSheetError = "Calculation sheet file size must be less than 10MB.";
      valid = false;
    }

    setFormDataError((prev) => ({ ...prev, ...errors }));
    return valid;
  };

  /** Submit form */

  const handleAddExpense = async () => {
    if (!validateForm()) return;

    try {
      setIsLoading(true);
      const fd = new FormData();
    
    
    

      // Required fields - match EXACTLY what API expects
  
      fd.append("Date", formData.Date);
      fd.append("Description", formData.Description);
      fd.append("Charges", formData.Charges);

      if (formData.Receipt) {
        // Make sure field name matches API exactly
        fd.append("ReceiptFile", formData.Receipt, formData.Receipt.name);
      }

      if (formData.CalculationSheet) {
        // Make sure field name matches API exactly
        fd.append(
          "CalculationSheetFile",
          formData.CalculationSheet,
          formData.CalculationSheet.name
        );
      }

      // Make the API call
      const response = await axiosInstance.post(
        API.expenseManagement.createExpenseManagementByEmp,fd    );
      toast.success("Expense added successfully", {
        autoClose: 2000,
        onClose: () => {
          clearFields();
          navigate("/employeedashboard/expenseManagement");
        },
      });
    } catch (error) {
      console.error("Error adding expense:", error);

      // Detailed error logging
      if (error.response) {
        console.error("Response status:", error.response.status);
        console.error("Response data:", error.response.data);

        // Check for validation errors
        if (error.response.data && error.response.data.errors) {
          console.error("Validation errors:", error.response.data.errors);

          // Display specific validation errors
          const errorMessages = [];
          for (const [field, messages] of Object.entries(error.response.data.errors)) {
            errorMessages.push(`${field}: ${messages.join(", ")}`);
          }
          toast.error(`Validation errors:\n${errorMessages.join("\n")}`);
        } else {
          toast.error(
            error.response.data?.title || error.response.data?.message || "Failed to add expense"
          );
        }
      } else if (error.request) {
        console.error("No response received:", error.request);
        toast.error("No response from server. Please try again.");
      } else {
        toast.error("Error: " + error.message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  /**  Clear fields */
  const clearFields = () => {
    setFormData({
      Date: "",
      Description: "",
      Charges: "",
      Receipt: "",
      CalculationSheet: "",
    });
  };

  return {
    formData,
    formDataError,
    handleChange,
    handleAddExpense,
    handleUploadDocChange,
    handleUploadCalculationDoc,
    isLoading,
  
  };
}

export default ExpenseManagementService;
